import React, { useCallback, useEffect, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import useHotJar from 'shared/hooks/useHotjar';
import { useGetConversationSummaryLazyQuery } from 'shared/types/graphql';
import Feedback from 'shared/components/Feedback';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { Box, HStack, useBreakpoint } from '@televet/kibble-ui/build/chakra';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useTracking } from 'shared/providers/TrackingProvider';
import { AiConversationSummaryEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useSummaryProvider } from 'shared/providers/SummaryProvider';

const NO_SUMMARY = 'No conversation summary available';

interface AiSummaryProps {
  channelId?: string;
}

const AiSummaryPopover = ({ channelId }: AiSummaryProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const { trackEvent: trackHotJarEvent } = useHotJar();
  const { updateSummaryButtonHidden, setFetchSummary, isSummaryButtonHidden } = useSummaryProvider();

  const currentBreakpoint = useBreakpoint();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const [getConversationSummary, { data: conversationSummaryData, loading: conversationSummaryDataIsLoading }] =
    useGetConversationSummaryLazyQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: GraphQLFetchPolicies.NoCache,
    });

  const fetchSummary = useCallback(() => {
    if (!channelId) return;
    trackEvent(AiConversationSummaryEvents.Open, { channelId });
    setIsPopoverOpen(true);
    getConversationSummary({
      variables: {
        where: {
          channelId,
        },
      },
    });
  }, [channelId, getConversationSummary, trackEvent]);

  useEffect(() => {
    setFetchSummary(() => fetchSummary);
  }, [fetchSummary, setFetchSummary]);

  const onClose = useCallback(
    (shouldTrack = true) => {
      if (shouldTrack) {
        trackEvent(AiConversationSummaryEvents.Close, { channelId });
      }
      setIsPopoverOpen(false);
    },
    [channelId, trackEvent],
  );

  // Close popover when changing channels
  useEffect(() => {
    onClose(false);
  }, [channelId, onClose]);

  const handleNegativeFeedback = useCallback(() => {
    trackHotJarEvent('AI Conversation Summary Negative Feedback');
    trackEvent(AiConversationSummaryEvents.NegativeFeedback, { channelId });
  }, [channelId, trackHotJarEvent, trackEvent]);

  const handlePositiveFeedback = useCallback(() => {
    trackHotJarEvent('AI Conversation Summary Positive Feedback');
    trackEvent(AiConversationSummaryEvents.PositiveFeedback, { channelId });
  }, [channelId, trackHotJarEvent, trackEvent]);

  const handleHideSummaryButton = useCallback(() => {
    updateSummaryButtonHidden(true);
    trackEvent(AiConversationSummaryEvents.HideButton, { channelId });
  }, [channelId, trackEvent, updateSummaryButtonHidden]);

  return (
    <Popover
      size={currentBreakpoint === 'base' ? 'lg' : 'xl'}
      variant="default"
      placement="bottom"
      isOpen={isPopoverOpen && !conversationSummaryDataIsLoading}
      isLazy
      headerProps={{
        children: (
          <HStack align="center">
            <Heading size="sm">AI Conversation Summary</Heading>
            <Tag size="sm" label="Beta" variant="info" />
          </HStack>
        ),
        onClose,
      }}
      anchor={
        <HStack spacing={1} display={isPopoverOpen || !isSummaryButtonHidden ? 'flex' : 'none'}>
          <Button
            variant="aiSecondary"
            size="sm"
            onClick={fetchSummary}
            iconName="magic"
            isLoading={conversationSummaryDataIsLoading}
          >
            Summarize Conversation
          </Button>
          {!isSummaryButtonHidden && !isPopoverOpen && (
            <Button variant="ghostNeutral" size="xs" iconName="close" onClick={handleHideSummaryButton} />
          )}
        </HStack>
      }
    >
      <Box p={3} maxH={180} overflowY="scroll" borderRadius="lg" bg="background.subtle">
        <Text size="sm">{conversationSummaryData?.aiChannelSummary?.parsedJson?.summary || NO_SUMMARY}</Text>
      </Box>
      <HStack justify="space-between" mt={4}>
        <Feedback
          label="How is this AI Summary?"
          onPositiveFeedback={handlePositiveFeedback}
          onNegativeFeedback={handleNegativeFeedback}
        />
        <Button onClick={onClose} size="sm">
          Close
        </Button>
      </HStack>
    </Popover>
  );
};

export default AiSummaryPopover;
